import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, ScanLine, BarChart3, Wallet, Layers, Award, FileSignature, Package, ShieldCheck, Settings, Megaphone, LogOut, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/fmt";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, roles: ["admin"], end: true },
  { to: "/admin/checkin", label: "Check-in", icon: ScanLine, roles: ["admin", "professor"] },
  { to: "/admin/alunos", label: "Alunos", icon: Users, roles: ["admin", "professor"] },
  { to: "/admin/frequencia", label: "Frequência", icon: BarChart3, roles: ["admin", "professor"] },
  { to: "/admin/financeiro", label: "Financeiro", icon: Wallet, roles: ["admin"] },
  { to: "/admin/planos", label: "Planos", icon: Layers, roles: ["admin"] },
  { to: "/admin/graduacao", label: "Graduação", icon: Award, roles: ["admin", "professor"] },
  { to: "/admin/contratos", label: "Contratos", icon: FileSignature, roles: ["admin"] },
  { to: "/admin/estoque", label: "Produtos e estoque", icon: Package, roles: ["admin"] },
  { to: "/admin/comunicados", label: "Comunicados", icon: Megaphone, roles: ["admin", "professor"] },
  { to: "/admin/usuarios", label: "Usuários", icon: ShieldCheck, roles: ["admin"] },
  { to: "/admin/configuracoes", label: "Configurações", icon: Settings, roles: ["admin"] },
];

function SideNav({ role, onNavigate }) {
  return (
    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
      {NAV.filter((n) => n.roles.includes(role)).map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={`nav-${n.to.split("/").pop() || "dashboard"}`}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
              isActive ? "bg-red-600 text-white shadow-[0_6px_20px_-8px_rgba(220,38,38,.8)]" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
          <n.icon className="w-4 h-4" /> {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const out = async () => {
    await logout();
    nav("/login");
  };
  const sidebar = (
    <div className="mat-texture h-full flex flex-col text-white">
      <div className="px-5 py-6 border-b border-white/10"><Logo /></div>
      <SideNav role={user.role} onNavigate={() => setOpen(false)} />
      <div className="p-4 border-t border-white/10">
        <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user.name}</div>
        <div className="text-[11px] uppercase tracking-wider text-red-500 font-bold">{ROLE_LABEL[user.role]}</div>
        <button onClick={out} data-testid="logout-btn" className="mt-3 flex items-center gap-2 text-xs text-zinc-400 hover:text-white transition-colors">
          <LogOut className="w-4 h-4" /> Sair
        </button>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:block w-[260px] fixed inset-y-0 left-0 z-30">{sidebar}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-[270px]">{sidebar}</div>
          <div className="flex-1 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="flex-1 lg:ml-[260px] min-w-0">
        <header className="lg:hidden sticky top-0 z-40 mat-texture text-white flex items-center justify-between px-4 h-14">
          <Logo compact />
          <span className="font-display font-bold tracking-wide">AMT GESTÃO</span>
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">{open ? <X /> : <Menu />}</button>
        </header>
        <main className="p-4 sm:p-6 lg:p-10 max-w-[1500px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
